/* oxlint-disable */
'use client';

import React, {
  useCallback, useRef, useState, useMemo,
} from 'react';

import Image from 'next/image';

import { useRouter } from 'next/navigation';

import { useCasinoSearch } from '@/features/v2/casino/hooks/useCasinoLobby';
import { HInput } from '@/stories/atoms/inputs/Input';

import { normalizeImageUrl } from '@/utils/core';

import { routes } from '../../constants/routes';

const Search = () => {
  const router = useRouter();
  const {
    query, suggestions, isSearching, setQuery, clear, debouncedQuery,
  } = useCasinoSearch();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const handleInputChange = useCallback((value: string) => {
    setQuery(value);
    setShowSuggestions(value.length > 0);
  }, [setQuery]);

  const handleSuggestionClick = useCallback((suggestion: any) => {
    setQuery(suggestion.title);
    setShowSuggestions(false);
    router.push(`${routes.CASINO}?view=games&search=${encodeURIComponent(suggestion.title)}`);
  }, [setQuery, router]);

  const handleClear = useCallback(() => {
    clear();
    setShowSuggestions(false);
    // Clear URL search parameter
    router.push(routes.CASINO);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [clear, router]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Enter' && debouncedQuery) {
      setShowSuggestions(false);
      router.push(`${routes.CASINO}?view=games&search=${encodeURIComponent(debouncedQuery)}`);
    }
  }, [debouncedQuery, router]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (
      suggestionsRef.current
      && !suggestionsRef.current.contains(e.target as Node)
      && inputRef.current
      && !inputRef.current.contains(e.target as Node)
    ) {
      setShowSuggestions(false);
    }
  }, []);

  const getGameImageUrl = useMemo(() => {
    return (suggestion: any) => {
      if (imageErrors.has(suggestion.identifier)) {
        return '/img/v2/casino/onError.webp';
      }
      return normalizeImageUrl(suggestion.thumb_url);
    };
  }, [imageErrors]);

  const handleImageError = useCallback((identifier: string) => {
    setImageErrors((prev) => { return new Set(prev).add(identifier); });
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div className="w-full mx-auto max-w-7xl flex gap-4 px-4 mt-7 relative">
      <div className="w-full max-w-7xl flex gap-4 relative">
        <div className="relative w-full">
          <HInput
            placeholder={['Encuentra tu juego favorito', 'Blackjack...', ' Redtiger...', 'Plinko...', 'Aviator...', 'Olympus...']}
            type="text"
            icon="search"
            theme="dark"
            height="thin"
            width="w-full"
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { return handleInputChange(e.target.value); }}
            onKeyDown={handleKeyDown}
            onFocus={() => { return setShowSuggestions(query.length > 0); }}
          />

          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          )}

          {isSearching && (
            <div className="absolute right-12 top-1/2 -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-hprimary-500" />
            </div>
          )}

          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute top-full left-0 right-0 mt-1 bg-default-50 border-b border-b-gray-200 py-1 px-2 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
            >
              {suggestions.map((suggestion) => {
                return (
                  <button
                    key={suggestion.identifier}
                    type="button"
                    onClick={() => { return handleSuggestionClick(suggestion); }}
                    className="w-full px-4 py-3 text-left hover:bg-default-100 border-b border-gray-100 last:border-b-0 flex items-center gap-3 bg-default-50"
                  >
                    <div className="relative w-8 h-8 shrink-0 rounded overflow-hidden">
                      <Image
                        src={getGameImageUrl(suggestion)}
                        alt={suggestion.title}
                        width={32}
                        height={32}
                        className="object-cover w-full h-full"
                        sizes="32px"
                        onError={() => { return handleImageError(suggestion.identifier); }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-default-900 truncate">
                        {suggestion.title}
                      </div>
                      <div className="text-sm text-default-600 truncate">
                        {suggestion.producer}
                        {suggestion.category && ` • ${suggestion.category}`}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;
